import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Flex, Grid, Text } from '@chakra-ui/react'
import { toaster } from '@minetrace/ui'
import { AnimatePresence, motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {
  LuArrowRight, LuBookOpen, LuDownload, LuFileStack, LuFolderOpen, LuOctagonAlert,
  LuSearch, LuShieldCheck, LuTruck, LuX,
} from 'react-icons/lu'
import { apiClient } from '../../../api/client'
import { expedienteService } from '../../../api/services'
import type { ExpedienteBatchSummary, ExpedienteSearchItemResponse } from '../../../api/models/response/expediente'
import { EXPEDIENTE_TYPE_BATCH } from '../../../api/models/response/expediente'
import { fonts, mineral, shadows } from '../../../theme'
import { formatElapsed, parseUtcDate } from '../../../lib/dates'
import { getShipmentStateBadge } from '../../../lib/shipmentStates'

/**
 * Auditoría de Emergencia — el auditor llega con UN dato (un código de lote, un
 * RUC, una placa, un nombre) y necesita el expediente oficial del traslado en
 * segundos. La pantalla está pensada como una mesa de auditoría:
 *
 *  - el buscador reconoce QUÉ tipo de dato escribiste (lote, RUC, placa, nombre);
 *  - cada lote muestra su cadena de custodia de un vistazo (qué tramos cumplió);
 *  - la ficha lateral desglosa esa cadena con fechas y tiempos entre tramos, y
 *    lleva directo al expediente, a su vista archivística o al ZIP.
 *
 * El detalle sigue siendo el expediente canónico: aquí no hay una capa paralela
 * de reportes, sólo una forma rápida de llegar a él.
 */

const acc = { color: mineral.lapis, deep: mineral.lapisDeep, soft: mineral.lapisSoft }

// ── Qué tipo de dato escribió el auditor ───────────────────────────────────
type Kind = 'lote' | 'ruc' | 'placa' | 'nombre'
const KIND_LABEL: Record<Kind, string> = { lote: 'Código de lote', ruc: 'RUC', placa: 'Placa', nombre: 'Razón social' }
function detectKind(q: string): Kind | null {
  const v = q.trim().toUpperCase()
  if (!v) return null
  if (/^KTR/.test(v) || /^\d{12,}$/.test(v)) return 'lote'
  if (/^\d{3,11}$/.test(v)) return 'ruc'
  if (/^[A-Z0-9]{3}-?\d{3}$/.test(v) || /^[A-Z]{1,3}\d-?\d{0,3}$/.test(v)) return 'placa'
  return 'nombre'
}

// ── Cadena de custodia ────────────────────────────────────────────────────
const CHAIN: Array<{ key: keyof ExpedienteBatchSummary; label: string; color: string }> = [
  { key: 'dispatchedAt', label: 'Despacho', color: mineral.cobre },
  { key: 'receivedAt', label: 'Recepción', color: mineral.oro },
  { key: 'weighedAt', label: 'Pesaje', color: mineral.turquesa },
  { key: 'sampledAt', label: 'Muestreo', color: mineral.turquesa },
  { key: 'labCompletedAt', label: 'Laboratorio', color: mineral.lapis },
  { key: 'reconciledAt', label: 'Conciliación', color: mineral.amatista },
  { key: 'settledAt', label: 'Liquidación', color: mineral.sabana },
]
const REJECTED = new Set([9, 10, 14])

type Verdict = 'sellado' | 'abierto' | 'alerta'
function verdictOf(r: ExpedienteSearchItemResponse): Verdict {
  if (r.status === 2 || (r.batch && REJECTED.has(r.batch.shipmentState))) return 'alerta'
  if (r.batch?.isSealed) return 'sellado'
  return 'abierto'
}
const VERDICT: Record<Verdict, { label: string; color: string; soft: string }> = {
  sellado: { label: 'Sellados', color: mineral.sabanaDeep, soft: mineral.sabanaSoft },
  abierto: { label: 'En curso', color: mineral.oroDeep, soft: mineral.oroSoft },
  alerta: { label: 'Con alerta', color: mineral.aji, soft: mineral.ajiSoft },
}

const shortDate = (iso: string) =>
  parseUtcDate(iso).toLocaleString('es-PE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
const weightOf = (b: ExpedienteBatchSummary) =>
  b.mineralWeight ? `${b.mineralWeight} ${b.measurementUnitName ?? ''}`.trim() : null

async function downloadZip(code: string) {
  const res = await apiClient.fetchAuthorized(expedienteService.getDownloadZipUrl(code))
  if (!res.ok) throw new Error(`No se pudo descargar (${res.status})`)
  const url = URL.createObjectURL(await res.blob())
  const a = document.createElement('a')
  a.href = url
  a.download = `${code}.zip`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** Siete tramos en una franja: lleno = cumplido. Rojo y cortado si el lote se rechazó. */
function ChainStrip({ b }: { b: ExpedienteBatchSummary }) {
  const rejected = REJECTED.has(b.shipmentState)
  const reached = CHAIN.filter(c => b[c.key]).length
  const lastDone = CHAIN.reduce((n, c, i) => (b[c.key] ? i : n), -1)
  return (
    <Flex gap="3px" align="center" title={`${reached} de ${CHAIN.length} tramos`}>
      {CHAIN.map((c, i) => {
        const done = !!b[c.key]
        const isBreak = rejected && i === lastDone + 1
        return (
          <Box key={c.key} h="6px" w="18px" borderRadius="2px"
            bg={isBreak ? mineral.aji : done ? c.color : mineral.sunk}
            opacity={done || isBreak ? 1 : 0.9} />
        )
      })}
      {b.isSealed && <Box ml={1} color={mineral.sabanaDeep} display="flex"><LuShieldCheck size={13} /></Box>}
    </Flex>
  )
}

export default function AuditoriaEmergenciaPage() {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)

  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [results, setResults] = useState<ExpedienteSearchItemResponse[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<Verdict | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [zipping, setZipping] = useState(false)

  const kind = detectKind(query)

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 300)
    return () => clearTimeout(id)
  }, [query])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    expedienteService.search(debounced, { type: EXPEDIENTE_TYPE_BATCH })
      .then(hits => { if (!cancelled) setResults(hits) })
      .catch(e => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Error al buscar expedientes de traslado')
          setResults([])
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [debounced])

  const counts = useMemo(() => {
    const c: Record<Verdict, number> = { sellado: 0, abierto: 0, alerta: 0 }
    for (const r of results ?? []) c[verdictOf(r)]++
    return c
  }, [results])

  const visible = useMemo(
    () => (results ?? []).filter(r => !filter || verdictOf(r) === filter),
    [results, filter],
  )
  const selected = visible.find(r => r.id === selectedId) ?? visible[0] ?? null

  const open = (r: ExpedienteSearchItemResponse, archival = false) =>
    navigate(`/app/identidad/expedientes/${encodeURIComponent(r.code)}${archival ? '/electronico' : ''}`)

  const onInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!visible.length) return
    const idx = Math.max(0, visible.findIndex(r => r.id === selected?.id))
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      const next = e.key === 'ArrowDown' ? Math.min(visible.length - 1, idx + 1) : Math.max(0, idx - 1)
      setSelectedId(visible[next]!.id)
      document.getElementById(`aud-row-${visible[next]!.id}`)?.scrollIntoView({ block: 'nearest' })
    } else if (e.key === 'Enter' && selected) {
      open(selected)
    }
  }

  const onZip = async (r: ExpedienteSearchItemResponse) => {
    setZipping(true)
    try {
      await downloadZip(r.code)
      toaster.success({ description: 'Expediente descargado' })
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'Error al descargar el ZIP' })
    } finally {
      setZipping(false)
    }
  }

  return (
    <Flex direction="column" gap={6}>
      {/* ── Encabezado + buscador en una sola franja ───────────────────────── */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <Flex align={{ base: 'stretch', lg: 'center' }} direction={{ base: 'column', lg: 'row' }} gap={{ base: 4, lg: 8 }}>
          <Flex align="center" gap={3} flexShrink={0}>
            <Flex w="36px" h="36px" borderRadius="10px" align="center" justify="center" bg={mineral.aji} color="#fff" flexShrink={0}>
              <LuOctagonAlert size={18} />
            </Flex>
            <Box>
              <Text fontFamily={fonts.display} fontWeight={800} fontSize="24px" letterSpacing="-0.03em" color={mineral.ink} lineHeight="1.1">
                Auditoría de Emergencia
              </Text>
              <Text fontSize="13px" color={mineral.ink3}>El expediente oficial del traslado y su cadena de custodia.</Text>
            </Box>
          </Flex>

          <Flex flex={1} maxW={{ lg: '620px' }} ml={{ lg: 'auto' }} align="center" gap={2.5} h="46px" px={4} borderRadius="12px" bg="#fff"
            border={`1px solid ${query ? acc.color : mineral.lineStrong}`} transition="border-color .2s, box-shadow .2s"
            boxShadow={query ? `0 0 0 3px ${acc.color}1a` : shadows.sm} position="relative" overflow="hidden">
            <Box color={query ? acc.color : mineral.ink3} display="flex" transition="color .2s"><LuSearch size={18} /></Box>
            <input
              ref={inputRef}
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={onInputKey}
              placeholder="Código de lote, RUC, placa o razón social"
              aria-label="Buscar expediente de traslado"
              style={{
                flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent',
                fontFamily: kind && kind !== 'nombre' ? fonts.mono : fonts.body, fontSize: 15, color: mineral.ink,
              }}
            />
            {/* Qué tipo de dato reconoció: sólo aparece mientras escribes */}
            <AnimatePresence>
              {kind && (
                <motion.span key={kind} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.15 }}
                  style={{ fontSize: 11.5, fontWeight: 600, color: acc.deep, background: acc.soft, padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                  {KIND_LABEL[kind]}
                </motion.span>
              )}
            </AnimatePresence>
            {query && (
              <Box as="button" {...{ type: 'button', 'aria-label': 'Limpiar búsqueda', title: 'Limpiar' }} display="flex" p={1} borderRadius="6px" color={mineral.ink3}
                cursor="pointer" _hover={{ bg: mineral.sunk, color: mineral.ink }}
                onClick={() => { setQuery(''); inputRef.current?.focus() }}>
                <LuX size={15} />
              </Box>
            )}
            {/* Barrido mientras busca: dice que la consulta está en curso */}
            <AnimatePresence>
              {loading && (
                <motion.span key="scan" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 2, overflow: 'hidden' }}>
                  <motion.span animate={{ x: ['-40%', '140%'] }} transition={{ duration: 1.1, repeat: Infinity, ease: 'easeInOut' }}
                    style={{ display: 'block', width: '40%', height: '100%', background: `linear-gradient(90deg, transparent, ${acc.color}, transparent)` }} />
                </motion.span>
              )}
            </AnimatePresence>
          </Flex>
        </Flex>
      </motion.div>

      {error && (
        <Box bg={mineral.ajiSoft} border={`1px solid ${mineral.aji}55`} borderRadius="14px" px={4} py={3}>
          <Text fontSize="sm" color={mineral.aji}>{error}</Text>
        </Box>
      )}

      {/* ── Resultados + ficha ──────────────────────────────────────────── */}
      <Grid templateColumns={{ base: '1fr', lg: 'minmax(0, 1fr) 400px', xl: 'minmax(0, 1fr) 440px' }} gap={6} alignItems="start">
        <Box minW={0}>
          {/* Filtros por veredicto, con su conteo */}
          <Flex align="center" gap={2} mb={3} wrap="wrap">
            <Text fontFamily={fonts.display} fontWeight={700} fontSize="17px" color={mineral.ink} mr={2}>
              {debounced ? 'Coincidencias' : 'Últimos expedientes'}
              <Text as="span" fontFamily={fonts.mono} fontSize="12px" fontWeight={500} color={mineral.ink3} ml={2}>{results?.length ?? 0}</Text>
            </Text>
            {(Object.keys(VERDICT) as Verdict[]).map(v => {
              const on = filter === v
              const cfg = VERDICT[v]
              return (
                <Flex key={v} as="button" {...{ type: 'button' }} align="center" gap={1.5} h="28px" px={3} borderRadius="full" cursor="pointer"
                  fontSize="12.5px" fontWeight={600} transition="all .15s"
                  bg={on ? cfg.color : cfg.soft} color={on ? '#fff' : cfg.color} border={`1px solid ${on ? cfg.color : 'transparent'}`}
                  onClick={() => setFilter(on ? null : v)} aria-pressed={on}>
                  {cfg.label}
                  <Text as="span" fontFamily={fonts.mono} fontSize="11px" opacity={0.85}>{counts[v]}</Text>
                </Flex>
              )
            })}
          </Flex>

          <Box borderRadius="18px" border={`1px solid ${mineral.line}`} bg={mineral.surface} boxShadow={shadows.sm} overflow="hidden">
            {results === null ? (
              <Box p={4}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <Box key={i} h="64px" mb={2} borderRadius="12px" bg={mineral.sunk} opacity={1 - i * 0.14} />
                ))}
              </Box>
            ) : visible.length === 0 ? (
              <Flex direction="column" align="center" py={14} px={6} gap={2} color={mineral.ink3}>
                <LuFolderOpen size={34} color={mineral.ink4} />
                <Text fontSize="14px" textAlign="center">
                  {debounced ? 'Ningún expediente de traslado coincide con esa búsqueda.' : 'No hay expedientes de traslado todavía.'}
                </Text>
              </Flex>
            ) : (
              visible.map((r, i) => {
                const b = r.batch
                const isSel = r.id === selected?.id
                const v = verdictOf(r)
                const badge = b ? getShipmentStateBadge(b.shipmentState) : null
                return (
                  <motion.div key={r.id} id={`aud-row-${r.id}`} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i, 12) * 0.025, duration: 0.2 }} style={{ position: 'relative' }}>
                    {isSel && (
                      <motion.span layoutId="aud-sel" transition={{ type: 'spring', stiffness: 500, damping: 40 }}
                        style={{ position: 'absolute', inset: 0, background: `${acc.color}0d`, borderLeft: `3px solid ${acc.color}` }} />
                    )}
                    <Grid position="relative" templateColumns={{ base: '1fr auto', md: 'minmax(0, 1.3fr) minmax(0, 1fr) 176px' }} gap={4} alignItems="center"
                      px={5} py={3.5} cursor="pointer" borderBottom={i < visible.length - 1 ? `1px solid ${mineral.line}` : 'none'}
                      _hover={isSel ? {} : { bg: mineral.surfaceAlt }}
                      onClick={() => (window.innerWidth < 992 ? open(r) : setSelectedId(r.id))}
                      onDoubleClick={() => open(r)}>
                      <Box minW={0}>
                        <Flex align="center" gap={2}>
                          <Box w="8px" h="8px" borderRadius="2px" transform="rotate(45deg)" bg={VERDICT[v].color} flexShrink={0} />
                          <Text fontFamily={fonts.mono} fontSize="13px" fontWeight={600} color={mineral.ink} whiteSpace="nowrap">{b?.batchCode ?? r.code}</Text>
                        </Flex>
                        <Text fontSize="13.5px" color={mineral.ink2} lineClamp={1} mt={0.5}>{r.companyLegalName ?? 'Proveedor sin vincular'}</Text>
                        <Flex gap={2} fontSize="11.5px" color={mineral.ink3} mt={0.5} minW={0}>
                          {r.companyRuc && <Text fontFamily={fonts.mono} whiteSpace="nowrap">{r.companyRuc}</Text>}
                          {b?.vehiclePlate && <><Text>·</Text><Text fontFamily={fonts.mono} whiteSpace="nowrap">{b.vehiclePlate}</Text></>}
                        </Flex>
                      </Box>
                      <Box display={{ base: 'none', md: 'block' }} minW={0}>
                        {b && <ChainStrip b={b} />}
                        <Text fontSize="11.5px" color={mineral.ink3} mt={1.5} lineClamp={1}>
                          {b ? <>{b.mineralTypeName ?? '—'}{weightOf(b) && <> · {weightOf(b)}</>} · {shortDate(b.dispatchedAt)}</> : `${r.itemCount} documentos`}
                        </Text>
                      </Box>
                      {badge && (
                        <Box px={2} py="3px" borderRadius="7px" fontSize="10px" fontWeight={700} textTransform="uppercase" letterSpacing="0.04em"
                          bg={badge.bg} color={badge.color} whiteSpace="nowrap" justifySelf="end" maxW="176px" overflow="hidden" textOverflow="ellipsis" title={badge.label}>{badge.label}</Box>
                      )}
                    </Grid>
                  </motion.div>
                )
              })
            )}
          </Box>
        </Box>

        {/* Ficha del lote seleccionado */}
        <Box display={{ base: 'none', lg: 'block' }} position="sticky" top="88px">
          <AnimatePresence mode="wait">
            {selected && (
              <motion.div key={selected.id} initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }}
                transition={{ duration: 0.22, ease: [0.2, 0.7, 0.2, 1] }}>
                <Dossier r={selected} zipping={zipping} onOpen={() => open(selected)} onArchival={() => open(selected, true)} onZip={() => onZip(selected)} />
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </Grid>
    </Flex>
  )
}

/** La carpeta del expediente: pestaña, sello, ruta, cadena de custodia y acciones. */
function Dossier({ r, zipping, onOpen, onArchival, onZip }: {
  r: ExpedienteSearchItemResponse; zipping: boolean; onOpen: () => void; onArchival: () => void; onZip: () => void
}) {
  const b = r.batch
  const v = verdictOf(r)
  const rejected = !!b && REJECTED.has(b.shipmentState)
  const steps = b ? CHAIN.map(c => ({ ...c, at: (b[c.key] as string | null) ?? null })) : []
  const lastDone = steps.reduce((acc2, s, i) => (s.at ? i : acc2), -1)

  return (
    <Box position="relative" pt="18px">
      {/* Pestaña de carpeta */}
      <Flex position="absolute" top={0} left="22px" h="26px" px={3} align="center" gap={1.5} borderTopRadius="10px"
        bg={mineral.surface} border={`1px solid ${mineral.line}`} borderBottom="none" fontFamily={fonts.mono} fontSize="10.5px"
        letterSpacing="0.1em" color={mineral.ink3} zIndex={1}>
        <LuFileStack size={12} /> EXPEDIENTE
      </Flex>

      <Box position="relative" borderRadius="18px" border={`1px solid ${mineral.line}`} bg={mineral.surface} boxShadow={shadows.lg} overflow="hidden">
        <Box h="4px" bg={VERDICT[v].color} />

        {/* Sello: cae sobre la carpeta cuando el lote está cerrado o en alerta */}
        {(b?.isSealed || v === 'alerta') && (
          <motion.div initial={{ scale: 1.8, opacity: 0, rotate: -24 }} animate={{ scale: 1, opacity: 1, rotate: -12 }}
            transition={{ type: 'spring', stiffness: 380, damping: 18, delay: 0.15 }}
            style={{ position: 'absolute', top: 22, right: 18, pointerEvents: 'none' }}>
            <Box px={3} py={1} border={`2px solid ${VERDICT[v].color}`} borderRadius="8px" color={VERDICT[v].color}
              fontFamily={fonts.mono} fontWeight={700} fontSize="12px" letterSpacing="0.16em" opacity={0.85}
              boxShadow={`inset 0 0 0 2px #fff, inset 0 0 0 3px ${VERDICT[v].color}55`}>
              {v === 'alerta' ? (r.status === 2 ? 'REVOCADO' : b?.shipmentState === 9 ? 'CANCELADO' : 'RECHAZADO') : 'SELLADO'}
            </Box>
          </motion.div>
        )}

        <Box px={6} pt={5} pb={4}>
          <Text fontFamily={fonts.mono} fontSize="17px" fontWeight={700} color={mineral.ink} letterSpacing="0.01em">{b?.batchCode ?? r.code}</Text>
          {b && b.batchCode !== r.code && <Text fontFamily={fonts.mono} fontSize="11px" color={mineral.ink4} mt={0.5}>{r.code}</Text>}
          <Text fontSize="15px" fontWeight={600} color={mineral.ink2} mt={2} lineClamp={2} pr={b?.isSealed || v === 'alerta' ? '90px' : 0}>
            {r.companyLegalName ?? 'Proveedor sin vincular'}
          </Text>
          {r.companyRuc && <Text fontFamily={fonts.mono} fontSize="12px" color={mineral.ink3}>RUC {r.companyRuc}</Text>}
        </Box>

        {b && (
          <>
            {/* Ruta: de la labor a la planta */}
            <Box mx={6} mb={4} p={3.5} borderRadius="14px" bg={mineral.surfaceAlt} border={`1px solid ${mineral.line}`}>
              <Grid templateColumns="minmax(0,1fr) auto minmax(0,1fr)" gap={2} alignItems="center">
                <Box minW={0}>
                  <Text fontSize="10px" fontFamily={fonts.mono} letterSpacing="0.12em" color={mineral.cobreDeep}>ORIGEN</Text>
                  <Text fontSize="13px" fontWeight={600} color={mineral.ink} lineClamp={2}>{b.miningRightName ?? '—'}</Text>
                  {b.miningRightCode && <Text fontFamily={fonts.mono} fontSize="10.5px" color={mineral.ink3}>{b.miningRightCode}</Text>}
                </Box>
                <Flex direction="column" align="center" color={mineral.oro} px={1}>
                  <LuTruck size={18} />
                  <Box w="34px" h="2px" mt={1} style={{ background: `repeating-linear-gradient(90deg, ${mineral.oro} 0 5px, transparent 5px 9px)` }} />
                </Flex>
                <Box minW={0} textAlign="right">
                  <Text fontSize="10px" fontFamily={fonts.mono} letterSpacing="0.12em" color={mineral.turquesaDeep}>DESTINO</Text>
                  <Text fontSize="13px" fontWeight={600} color={mineral.ink} lineClamp={2}>{b.collectionCenterName ?? '—'}</Text>
                </Box>
              </Grid>
              <Grid templateColumns="repeat(3, minmax(0,1fr))" gap={2} mt={3} pt={3} borderTop={`1px dashed ${mineral.lineStrong}`}>
                {[
                  { k: 'Mineral', v: b.mineralTypeName },
                  { k: 'Peso', v: weightOf(b) },
                  { k: 'Placa', v: b.vehiclePlate, mono: true },
                ].map(f => (
                  <Box key={f.k} minW={0}>
                    <Text fontSize="10.5px" color={mineral.ink3}>{f.k}</Text>
                    <Text fontSize="12.5px" fontWeight={600} color={mineral.ink} fontFamily={f.mono ? fonts.mono : undefined} lineClamp={1}>{f.v ?? '—'}</Text>
                  </Box>
                ))}
              </Grid>
              {b.driverName && <Text fontSize="11.5px" color={mineral.ink3} mt={2} lineClamp={1}>Conductor: {b.driverName}</Text>}
            </Box>

            {/* Cadena de custodia */}
            <Box px={6} pb={4}>
              <Flex justify="space-between" align="baseline" mb={2.5}>
                <Text fontFamily={fonts.display} fontWeight={700} fontSize="14px" color={mineral.ink}>Cadena de custodia</Text>
                <Text fontFamily={fonts.mono} fontSize="11px" color={mineral.ink3}>{lastDone + 1}/{CHAIN.length}</Text>
              </Flex>
              {steps.map((s, i) => {
                const prev = steps.slice(0, i).reverse().find(p => p.at)
                const gap = s.at && prev?.at ? (parseUtcDate(s.at).getTime() - parseUtcDate(prev.at).getTime()) / 60000 : null
                const isBreak = rejected && i === lastDone + 1
                const color = isBreak ? mineral.aji : s.at ? s.color : mineral.ink4
                return (
                  <Flex key={s.key} gap={3} align="stretch">
                    <Flex direction="column" align="center" w="14px" flexShrink={0}>
                      <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.05 * i + 0.1, type: 'spring', stiffness: 500, damping: 22 }}
                        style={{
                          width: 12, height: 12, borderRadius: 3, transform: 'rotate(45deg)', marginTop: 4, flexShrink: 0,
                          background: s.at || isBreak ? color : '#fff', border: `2px solid ${color}`,
                        }} />
                      {i < steps.length - 1 && (
                        <motion.span initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ delay: 0.05 * i + 0.15, duration: 0.2 }}
                          style={{ flex: 1, width: 2, minHeight: 14, transformOrigin: 'top', background: s.at && steps[i + 1]!.at ? s.color : mineral.line }} />
                      )}
                    </Flex>
                    <Flex flex={1} justify="space-between" gap={2} pb={2.5} minW={0}>
                      <Text fontSize="13px" fontWeight={s.at ? 600 : 500} color={isBreak ? mineral.aji : s.at ? mineral.ink : mineral.ink4}>
                        {isBreak ? `${s.label} · no se cumplió` : s.label}
                      </Text>
                      <Box textAlign="right" flexShrink={0}>
                        {s.at && <Text fontSize="12px" color={mineral.ink2} whiteSpace="nowrap">{shortDate(s.at)}</Text>}
                        {gap !== null && gap >= 1 && <Text fontFamily={fonts.mono} fontSize="10.5px" color={mineral.ink4}>+{formatElapsed(gap)}</Text>}
                      </Box>
                    </Flex>
                  </Flex>
                )
              })}
            </Box>
          </>
        )}

        {/* Acciones */}
        <Box px={6} py={4} borderTop={`1px solid ${mineral.line}`} bg={mineral.surfaceAlt}>
          <Flex as="button" {...{ type: 'button' }} w="100%" h="44px" align="center" justify="center" gap={2} borderRadius="12px" cursor="pointer"
            bg={acc.color} color="#fff" fontWeight={600} fontSize="14px" transition="background .15s" _hover={{ bg: acc.deep }}
            role="group" onClick={onOpen}>
            Abrir expediente
            <Box display="flex" transition="transform .2s" _groupHover={{ transform: 'translateX(3px)' }}><LuArrowRight size={16} /></Box>
          </Flex>
          <Grid templateColumns="1fr 1fr" gap={2} mt={2}>
            <Flex as="button" {...{ type: 'button' }} h="38px" align="center" justify="center" gap={1.5} borderRadius="10px" cursor="pointer"
              border={`1px solid ${mineral.line}`} bg="#fff" color={mineral.ink2} fontSize="12.5px" fontWeight={600}
              _hover={{ borderColor: acc.color, color: acc.deep }} transition="all .15s" onClick={onArchival}>
              <LuBookOpen size={14} /> Índice digital
            </Flex>
            <Flex as="button" {...{ type: 'button', disabled: zipping }} h="38px" align="center" justify="center" gap={1.5} borderRadius="10px"
              cursor={zipping ? 'progress' : 'pointer'} border={`1px solid ${mineral.line}`} bg="#fff" color={mineral.ink2} fontSize="12.5px" fontWeight={600}
              _hover={{ borderColor: acc.color, color: acc.deep }} transition="all .15s" onClick={onZip}>
              {zipping
                ? <motion.span animate={{ y: [0, 3, 0] }} transition={{ duration: 0.7, repeat: Infinity }} style={{ display: 'flex' }}><LuDownload size={14} /></motion.span>
                : <LuDownload size={14} />}
              {zipping ? 'Preparando…' : 'Descargar ZIP'}
            </Flex>
          </Grid>
          <Text fontSize="11px" color={mineral.ink3} textAlign="center" mt={2.5}>{r.itemCount} documentos en el expediente</Text>
        </Box>
      </Box>
    </Box>
  )
}
